import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { merge } from 'rxjs';

import { ConfirmationService, MessageService, TableState } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';

import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@ls-commons/auth/services';
import {
  ButtonIconAtomComponent,
  CardAtomComponent,
  InputAtomComponent,
  ProgressSpinnerAtomComponent,
  ToastAtomComponent,
} from '@ls-commons/components';
import { InstallationService } from '@ls-commons/installations';
import { AuthFacade } from '@ls-commons/login/store';
import { ASSIGNABLE_ROLES, getRoleLabel, UserAuth } from '@ls-commons/models';
import {
  buildInstallationsWithAssignment,
  UserListExtra,
  UserService,
} from '@ls-commons/users/services';
import { UsersFacade } from '@ls-commons/users/store';
import { ModalService } from '@ls-commons/utils/modals';
import { Button } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { InstallationUsersViewComponent } from '../installation-users-view/installation-users-view.component';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { UserInstallationAssignComponent } from '../user-installation-assign/user-installation-assign.component';

type ColumnType = 'text' | 'role' | 'boolean' | 'date';

interface Column {
  field: string;
  header: string;
  type: ColumnType;
  customExportHeader?: string;
}

interface FilterOption {
  label: string;
  value: number | boolean;
}

// _v2: invalida un ancho de columna/tabla guardado con columnResizeMode
// "expand" (dejaba la tabla crecer más allá de su contenedor a propósito,
// y ese ancho ya expandido se perpetuaba al guardarse) -- ver mismo fix en
// ordenes-trabajo.component.ts.
const STATE_KEY = 'tableState_users_v2';
/** Umbral para la tarjeta "Sin conexión reciente" -- ver getters
 * inactiveUsersCount más abajo. */
const INACTIVE_DAYS = 30;
@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    DialogModule,
    ConfirmDialogModule,
    FormsModule,
    TagModule,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Toast,
    InputAtomComponent,
    ToastAtomComponent,
    ButtonIconAtomComponent,
    CardAtomComponent,
    ProgressSpinnerAtomComponent,
    InstallationUsersViewComponent,
    Button,
    SelectModule,
    Tooltip,
  ],
  providers: [
    ConfirmationService,
    DialogService,
    UsersFacade,
    ModalService,
    AuthFacade,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListComponent implements OnInit {
  confirmationService = inject(ConfirmationService);
  dialogService = inject(DialogService);
  userService = inject(UserService);
  modalService = inject(ModalService);
  destroyRef = inject(DestroyRef);
  route = inject(ActivatedRoute);
  customForm = null;
  readonly usersFacade = inject(UsersFacade);
  private readonly router = inject(Router);
  authFacade = inject(AuthFacade);
  public authService = inject(AuthService);
  readonly installationService = inject(InstallationService);
  private messageService = inject(MessageService);

  isLoading = this.usersFacade.loading;

  activeView = signal<'usuarios' | 'instalacion'>('usuarios');

  installationsData: any[] = [];

  user!: UserAuth | null;
  cols!: Column[];
  users!: UserAuth[];
  installationsUsersData: any[] = [];

  readonly stateKey = STATE_KEY;

  // TS-371: opciones de los filtros de columna que no son texto libre --
  // Público (4) no aparece en esta lista en la práctica, se deja fuera
  // para no ofrecer una opción que nunca filtra nada.
  readonly roleOptions: FilterOption[] = ASSIGNABLE_ROLES.map(role => ({
    label: getRoleLabel(role),
    value: role,
  }));
  readonly pushOptions: FilterOption[] = [
    { label: 'Activado', value: true },
    { label: 'Desactivado', value: false },
  ];

  // ── Tarjetas de resumen de la cabecera -- se recalculan solas al leer
  // (los datos ya están en `users`, no hace falta duplicar estado).
  get totalUsersCount(): number {
    return this.users?.length ?? 0;
  }

  get pushEnabledCount(): number {
    return (this.users ?? []).filter((u: any) => u.pushEnabled).length;
  }

  get pushEnabledPercent(): number {
    return this.totalUsersCount
      ? Math.round((this.pushEnabledCount / this.totalUsersCount) * 100)
      : 0;
  }

  get inactiveUsersCount(): number {
    const threshold = Date.now() - INACTIVE_DAYS * 24 * 60 * 60 * 1000;
    return (this.users ?? []).filter(
      (u: any) => !u.lastLoginAt || u.lastLoginAt.getTime() < threshold
    ).length;
  }

  constructor() {
    this.route.data.subscribe(data => {
      this.customForm = data && data['customForm'];
    });
  }

  ngOnInit() {
    this.repairPersistedTableState();

    this.cols = [
      { field: 'nombre', header: 'Nombre', type: 'text' },
      { field: 'apellidos', header: 'Apellidos', type: 'text' },
      { field: 'email', header: 'Email', type: 'text' },
      { field: 'role', header: 'Tipo', type: 'role' },
      { field: 'pushEnabled', header: 'Push', type: 'boolean' },
      { field: 'lastLoginAt', header: 'Última conexión', type: 'date' },
    ];

    this.loadInstallations();
    this.getInstallationsUsers();
    merge(
      this.usersFacade.usersError$,
      this.usersFacade.deleteError$
      // this.authFacade.selectResetPasswordError$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ error }) => {
        error && this.modalService.showErrorMessage(error);
      });

    this.usersFacade.deletedUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ user, deleted }) => {
        if (deleted) {
          this.modalService.showSuccessMessage(
            `Usuario ${user?.nombre ?? ''} eliminado`
          );
          this.usersFacade.getUsers();
        }
      });

    // this.authFacade.selecResetPasswordResponse$
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(({ response }) => {
    //     if (response?.msg) {
    //       this.modalService.showAlertButtonLink(
    //         'success',
    //         '',
    //         response.msg,
    //         'Cerrar'
    //       );
    //     }
    //   });

    this.usersFacade.selecUsers$.subscribe(users => {
      const isSuper = this.authService.currentUser()?.role === 1;
      let filteredUsers: any = isSuper
        ? users
        : users.filter((element: any) => element.role !== 1);

      // TS-371-fix: cada recarga (p.ej. al cerrar el modal de instalaciones
      // asignadas) trae usuarios nuevos SIN pushEnabled/lastLoginAt todavía
      // -- se piden aparte, en otra llamada. Sin esto, las columnas Push /
      // Última conexión y las tarjetas de resumen parpadeaban un instante a
      // sus valores por defecto (todo "Nunca"/sin push) hasta que esa
      // segunda llamada terminaba. Se arrastra lo que ya sabíamos mientras
      // llega la versión fresca.
      const previousExtrasByUser = new Map(
        (this.users ?? []).map((u: any) => [
          u.id,
          { pushEnabled: u.pushEnabled, lastLoginAt: u.lastLoginAt },
        ])
      );
      this.users = filteredUsers.map((user: any) => ({
        ...user,
        ...previousExtrasByUser.get(user.id),
      }));
      this.loadUserExtras();
    });

    this.authFacade.selecAuthResponse$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data.response?.msg && data.type === '[Auth] new Password success') {
          this.modalService.showSuccessMessage(data.response.msg);
        }
      });

    this.usersFacade.getUsers();
  }

  /** Estado de push + última conexión (TS-371) -- se piden en un segundo
   * viaje aparte a Backend-API porque `usuario` (con el resto de datos de
   * esta tabla) lo sirve panel-nodejs, que no tiene acceso a
   * push_subscriptions/login_log. Si falla, la tabla se queda sin esas
   * dos columnas rellenas en vez de romper el resto de la página. */
  private loadUserExtras(): void {
    if (!this.users?.length) return;

    const usuarioIds = this.users.map(u => u.id);
    this.userService.getListExtras(usuarioIds).subscribe({
      next: (extras: UserListExtra[]) => {
        const extrasByUser = new Map(extras.map(e => [e.usuarioId, e]));
        this.users = this.users.map(user => {
          const extra = extrasByUser.get(user.id);
          return {
            ...user,
            pushEnabled: extra?.pushEnabled ?? false,
            lastLoginAt: extra?.lastLoginAt ? new Date(extra.lastLoginAt) : null,
          };
        }) as any;
      },
      error: () => {},
    });
  }

  /** "Hace X" en español, con la misma escala habitual (minutos -> horas ->
   * días -> meses -> años) -- null significa que nunca ha entrado. */
  timeAgo(date: Date | null): string {
    if (!date) return 'Nunca';

    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Hace un momento';

    const ranges: [number, string][] = [
      [60, 'minuto'],
      [60, 'hora'],
      [24, 'día'],
      [30, 'mes'],
      [12, 'año'],
    ];

    let value = seconds;
    let unit = 'segundo';
    for (const [factor, nextUnit] of ranges) {
      if (value < factor) break;
      value = Math.floor(value / factor);
      unit = nextUnit;
    }

    const plural = value === 1 ? '' : unit.endsWith('s') ? '' : unit === 'mes' ? 'es' : 's';
    return `Hace ${value} ${unit}${plural}`;
  }

  /** Persiste ancho de columnas y paginación, pero NUNCA filtros/orden
   * (mismo criterio que installation-alarms.saveTableState): si se
   * guardaran los filtros aquí, al recargar la tabla podría quedarse
   * filtrada sin que se vea en ningún sitio por qué faltan filas.
   *
   * OJO: `first`/`rows` tienen que ir siempre con un valor -- si el
   * paginador está activo, `restoreState()` de PrimeNG los sobreescribe
   * igualmente aunque no existan en el objeto guardado (los deja en
   * `undefined`), lo que rompía la paginación ("Mostrando 1 - NaN").
   */
  saveTableState($event: TableState): void {
    localStorage.setItem(
      this.stateKey,
      JSON.stringify({
        first: $event.first ?? 0,
        rows: $event.rows ?? 10,
        columnWidths: $event.columnWidths,
        tableWidth: $event.tableWidth,
      })
    );
  }

  /** Repara en caliente un `tableState_users` que se hubiera guardado sin
   * `first`/`rows` (ver saveTableState) antes de este arreglo -- se
   * ejecuta al arrancar el componente, antes de que <p-table> restaure su
   * propio estado, para que nadie se quede con la tabla rota sin tener
   * que borrar a mano el localStorage. */
  private repairPersistedTableState(): void {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return;

    try {
      const state = JSON.parse(raw);
      if (state.first !== undefined && state.rows !== undefined) return;

      localStorage.setItem(
        STATE_KEY,
        JSON.stringify({
          ...state,
          first: state.first ?? 0,
          rows: state.rows ?? 10,
        })
      );
    } catch {
      localStorage.removeItem(STATE_KEY);
    }
  }

  getInstallationsUsers() {
    this.userService.getInstallationsUsers().subscribe({
      next: (x: any) => {
        this.installationsUsersData = x;
      },
      error: (error: any) => {},
    });
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  exportToExcel(table: Table) {
    table.exportCSV();
  }

  loadInstallations() {
    this.installationService.getInstallations().subscribe((data: any) => {
      this.installationsData = data.data;
    });
  }

  deleteUser(user: any) {
    this.confirmationService.confirm({
      message: `Seguro que quiere eliminar el usuario ${user.nombre} ?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersFacade.deleteUser(user);
      },
    });
  }

  sendResetPasswordEmail(user: any) {
    this.confirmationService.confirm({
      message: `Seguro que quiere enviar el correo de restablecimiento de contraseña al usuario ${user.nombre} ?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.authFacade.resetPassword(user.email);
      },
    });
  }

  editInstallationAssigned(userData: any) {
    const installationsData = this.handleData(userData.id);

    const ref = this.dialogService.open(UserInstallationAssignComponent, {
      dismissableMask: false,
      showHeader: false,
      data: {
        userId: userData.id,
        userData: userData,
        installationsData: installationsData,
      },
      modal: true,
      width: '480px',
      closable: true,
      // TS-371-fix: ver comentario junto a `duplicate` en editUser().
      duplicate: true,
    });
    ref.onClose.subscribe((reload: boolean) => {
      reload && this.usersFacade.getUsers();
      this.getInstallationsUsers();
    });
  }

  handleData(userId: string | null) {
    return buildInstallationsWithAssignment(
      this.installationsData,
      this.installationsUsersData,
      userId
    );
  }

  resetPassword(user: UserAuth) {
    this.confirmationService.confirm({
      message: `Seguro que quiere restablecer la contraseña para el usuario ${user.nombre} ?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        // this.authFacade.resetPassword(user.email);
      },
    });
  }

  editUser(userId: string | null) {
    const installationsData = this.handleData(userId);

    const ref = this.dialogService.open(UserEditComponent, {
      dismissableMask: false,
      header: userId === null ? 'Nuevo Usuario' : 'Modificar Usuario',
      data: {
        userId,
        installationsData,
      },
      width: '600px',
      // TS-371-fix: DialogService rechaza en silencio abrir un segundo
      // diálogo del MISMO componente si el anterior aún no ha terminado
      // su animación de cierre (lo controla con una lista interna que
      // solo limpia al acabar esa animación) -- `open()` devuelve `null`
      // sin avisar, así que el primer clic tras cerrar no hacía nada y
      // hacían falta dos. `duplicate: true` salta esa comprobación, que
      // aquí no protege de nada real (cada fila abre su propio diálogo).
      duplicate: true,
    });
    ref.onClose.subscribe((reload: boolean) => {
      reload && this.usersFacade.getUsers();
    });
  }

  openCustomForm(user: UserAuth) {
    if (this.customForm) {
      const ref = this.dialogService.open(this.customForm, {
        dismissableMask: false,
        header: user === null ? 'Nuevo Usuario' : 'Modificar Usuario',
        data: {
          user,
        },
        width: '600px',
        duplicate: true,
      });
      ref.onClose.subscribe((reload: boolean) => {
        reload && this.usersFacade.getUsers();
      });
    }
  }

  getTypeName(id: number) {
    return getRoleLabel(id);
  }

  /**
   * Abre el historial de navegacion de ese usuario (ticket #293).
   *
   * Navega a la pantalla de la aplicacion con el usuario en la URL: esta lib es
   * compartida y no puede importar componentes de `apps/panel-angular`.
   */
  viewNavigationHistory(rowData: any): void {
    this.router.navigate(['/dashboard/navigation-history'], {
      queryParams: { user: rowData.id },
    });
  }

  goToAdmin(): void {
    this.router.navigate(['/dashboard/admin']);
  }
}
